import { useState, useEffect, useCallback, useRef } from "react";
import { Session, User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { isInGracePeriod } from "@/lib/featureGating";

export interface Subscription {
  id: string;
  user_id: string;
  stripe_customer_id: string;
  stripe_subscription_id?: string;
  plan: "free" | "lite" | "standard" | "premium" | "pro" | "enterprise";
  period?: "monthly" | "yearly" | "onetime"; // 'onetime' = Pro/Enterprise 1-year prepaid, no subscription
  trial_started_at?: string;
  trial_ends_at?: string;
  current_period_start?: string;
  current_period_end?: string;
  status: "active" | "canceled" | "past_due" | "incomplete" | "incomplete_expired" | "unpaid";
  /** Purchased extra team seats above the plan's included base (Enterprise per-seat
   *  overage). 0/undefined for everyone else. Effective cap = base(plan) + extra_seats. */
  extra_seats?: number;
  created_at: string;
  updated_at: string;
}

interface UseSubscriptionOptions {
  user: User | null;
  session: Session | null;
  loading: boolean;
}

// Plan precedence for merging a user's own plan with an inherited org-seat plan.
const PLAN_RANK: Record<string, number> = {
  free: 0, lite: 1, standard: 2, premium: 3, pro: 4, enterprise: 5,
};

export const useSubscription = ({ user, session, loading }: UseSubscriptionOptions) => {
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  // True when the user is covered by an org seat (a teammate-owner's Pro/Enterprise
  // licence). Such members must NOT buy their own subscription — gated in the UI
  // and authoritatively in the checkout edge functions.
  const [isOrgSeat, setIsOrgSeat] = useState(false);
  const isMountedRef = useRef(true);

  // Component-lifetime mount flag. Previously this ref was flipped inside
  // the realtime effect's cleanup, which broke the visibility-refresh
  // and other effects that share the same ref: after the realtime effect
  // re-ran (e.g. user.id changed), isMountedRef stayed false for the
  // remainder of the component's life. Decoupling fixes that.
  useEffect(() => {
    isMountedRef.current = true;
    return () => { isMountedRef.current = false; };
  }, []);

  // Org-licence inheritance (Pro/Enterprise): a member of a team whose owner
  // holds an active Pro/Enterprise plan inherits that tier as a seat — the owner
  // pays, the member needs no subscription. RLS hides the owner's row, so a
  // SECURITY DEFINER RPC resolves the tier server-side (scoped to the caller's
  // active, non-owned teams). Fail-safe: any error → null → keep own plan.
  const resolveOrgSeatPlan = useCallback(async (): Promise<"pro" | "enterprise" | null> => {
    try {
      const { data, error } = await supabase.rpc("my_org_seat_plan" as never);
      if (error) return null;
      return data === "enterprise" || data === "pro" ? data : null;
    } catch {
      return null;
    }
  }, []);

  // Merge the user's OWN subscription with any inherited org seat, keeping the
  // higher tier, then set state. Downstream gating reads `subscription.plan`, so
  // a seated member gets a synthetic active subscription at the org tier and
  // every entitlement check works unchanged.
  const applyEffectiveSubscription = useCallback(
    async (userId: string, ownSub: Subscription | null) => {
      const ownPlan = ownSub && ownSub.status === "active" ? ownSub.plan : "free";
      const orgPlan = await resolveOrgSeatPlan();
      if (!isMountedRef.current) return;
      setIsOrgSeat(!!orgPlan);
      if (orgPlan && PLAN_RANK[orgPlan] > (PLAN_RANK[ownPlan] ?? 0)) {
        setSubscription({
          id: ownSub?.id ?? `org-seat:${userId}`,
          user_id: userId,
          stripe_customer_id: ownSub?.stripe_customer_id ?? "",
          plan: orgPlan,
          status: "active",
          period: ownSub?.period,
          created_at: ownSub?.created_at ?? new Date().toISOString(),
          updated_at: new Date().toISOString(),
        });
      } else {
        setSubscription(ownSub);
      }
    },
    [resolveOrgSeatPlan],
  );

  const fetchSubscription = useCallback(async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from("subscriptions")
        .select("*")
        .eq("user_id", userId)
        .maybeSingle();

      if (!isMountedRef.current) return;

      let ownSub: Subscription | null = null;
      if (!error && data) {
        ownSub = data;
      } else if (error) {
        if (error.code === "PGRST116") {
          // .maybeSingle() returns PGRST116 only when multiple rows match
          // (zero rows is silent). Webhook bugs in the past produced
          // duplicate subscription rows per user; prefer an active one
          // over a canceled/incomplete leftover, then fall back to latest.
          console.warn("Multiple subscription rows found for user, picking active or latest");
          const { data: rows } = await supabase
            .from("subscriptions")
            .select("*")
            .eq("user_id", userId)
            .order("updated_at", { ascending: false });
          if (!isMountedRef.current) return;
          if (rows && rows.length > 0) {
            ownSub = rows.find((r) => r.status === "active") ?? rows[0];
          }
        } else {
          console.warn("Subscription fetch error:", error.code);
        }
      }

      // Apply org-seat inheritance on top of the user's own plan.
      await applyEffectiveSubscription(userId, ownSub);
    } catch (err) {
      // Silently ignore AbortError - these are normal when navigation happens
      if (err instanceof Error && err.name === 'AbortError') {
        console.debug('Subscription fetch aborted');
        return;
      }
      console.error("Subscription fetch error:", err);
    }
  }, [applyEffectiveSubscription]);

  const clearSubscription = useCallback(() => {
    setSubscription(null);
    setIsOrgSeat(false);
  }, []);

  // Set up real-time listener for subscription updates.
  // Channel handle declared OUTSIDE the try block so the cleanup can
  // unsubscribe even if a later `.on(...)` call throws — otherwise the first
  // partially-constructed channel would stay subscribed for the rest of the
  // component's life on every subsequent re-mount.
  useEffect(() => {
    if (!user?.id) return;

    let channelHandle: ReturnType<typeof supabase.channel> | null = null;

    try {
      const channelName = `public:subscriptions:user_id=eq.${user.id}`;

      channelHandle = supabase
        .channel(channelName)
        .on(
          'postgres_changes',
          {
            event: '*', // Listen to all events (INSERT, UPDATE, DELETE)
            schema: 'public',
            table: 'subscriptions',
            filter: `user_id=eq.${user.id}`,
          },
          (payload: any) => {
            // Realtime payloads can arrive empty when REPLICA IDENTITY isn't
            // set to FULL on the source table — payload.new ends up as `{}`
            // for UPDATE/INSERT, which would otherwise replace a real
            // subscription with an empty object and silently demote the user
            // to "free". Require the canonical fields before applying.
            const next = payload?.new;
            if (next && typeof next === "object" && next.id && next.user_id && next.plan && next.status) {
              setSubscription(next as Subscription);
              return;
            }
            // DELETE arrives with empty new + populated old — clear state.
            if (payload?.eventType === "DELETE" && payload?.old) {
              setSubscription(null);
              return;
            }
            // Anything else: refetch authoritative state instead of trusting
            // a partial payload. user.id is guaranteed present here.
            if (user?.id) fetchSubscription(user.id);
          }
        )
        .subscribe((status) => {
          if (status === 'CLOSED') {
            console.debug('Real-time subscription channel closed (may reconnect)');
          } else if (status === 'CHANNEL_ERROR') {
            console.debug('Real-time subscription channel error (may retry)');
          }
        });

      // Cleanup both happy and partial-construction paths.
      return () => {
        try { channelHandle?.unsubscribe(); } catch { /* ignore unsubscribe errors */ }
      };
    } catch (err) {
      console.debug('Error setting up real-time subscription:', err);
      return () => {
        try { channelHandle?.unsubscribe(); } catch { /* ignore unsubscribe errors */ }
      };
    }
  }, [user?.id, fetchSubscription]);

  // Heartbeat: update last_seen_at in subscriptions every 5 minutes
  useEffect(() => {
    if (!user?.id) return;

    const updateLastSeen = () => {
      supabase
        .from('subscriptions')
        .update({ last_seen_at: new Date().toISOString() } as Record<string, unknown>)
        .eq('user_id', user.id)
        .then(() => {})
        .catch((err: unknown) => {
          console.warn('Heartbeat update failed:', err instanceof Error ? err.message : err);
        });
    };

    updateLastSeen(); // immediately on login
    const interval = setInterval(updateLastSeen, 5 * 60 * 1000); // every 5 min
    return () => clearInterval(interval);
  }, [user?.id]);

  // Refresh subscription when page becomes visible (user returns from Stripe checkout).
  // The pendingUpgrade flag is stored as `<expiresAtMs>` so a stale entry from a
  // closed tab (user abandoned Stripe) doesn't trigger a phantom refetch
  // weeks later. 30 minutes covers the longest realistic checkout flow.
  useEffect(() => {
    if (!user?.id) return;

    const PENDING_UPGRADE_TTL_MS = 30 * 60 * 1000;

    const handleVisibilityChange = async () => {
      if (document.visibilityState !== 'visible' || !isMountedRef.current) return;
      const raw = localStorage.getItem('pendingUpgrade');
      if (!raw) return;

      // Backward-compatible: legacy entries had no timestamp; treat them as
      // expired so we clear without firing an extra request.
      const expiresAt = Number(raw);
      const expired = !Number.isFinite(expiresAt) || expiresAt < Date.now();
      if (expired) {
        try { localStorage.removeItem('pendingUpgrade'); } catch { /* ignore */ }
        return;
      }

      await fetchSubscription(user.id);
      if (isMountedRef.current) {
        try { localStorage.removeItem('pendingUpgrade'); } catch { /* ignore */ }
      }
      // The TTL constant is referenced from the Stripe checkout call site
      // (whichever module calls `localStorage.setItem('pendingUpgrade', String(Date.now() + ttl))`).
      void PENDING_UPGRADE_TTL_MS;
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [user?.id, fetchSubscription]);

  // --- Subscription helper functions ---

  // Free plan never expires — always returns 0
  const getTrialDaysRemaining = useCallback(() => 0, []);

  // Free plan never expires — always returns false
  const isTrialExpired = useCallback(() => false, []);

  const isLite = useCallback(() => {
    return subscription?.plan === "lite" && subscription?.status === "active";
  }, [subscription]);

  const isStandard = useCallback(() => {
    return subscription?.plan === "standard" && subscription?.status === "active";
  }, [subscription]);

  const isPremium = useCallback(() => {
    return subscription?.plan === "premium" && subscription?.status === "active";
  }, [subscription]);

  // "Pro or above": Enterprise is a strict superset of Pro, so every
  // `isPremium || isPro` feature gate (multi-floor, portal, validator, BOM,
  // DXF, smart-quote) passes for Enterprise too WITHOUT editing the
  // parallel-owned bag builders. Enterprise-only limits (10 seats, white-label,
  // unlimited companies) are resolved by plan name via featureGating helpers.
  const isPro = useCallback(() => {
    return (subscription?.plan === "pro" || subscription?.plan === "enterprise")
      && subscription?.status === "active";
  }, [subscription]);

  const isEnterprise = useCallback(() => {
    return subscription?.plan === "enterprise" && subscription?.status === "active";
  }, [subscription]);

  const isPaid = useCallback(() => {
    if (!subscription || subscription.plan === "free") return false;
    if (subscription.status === "active") return true;
    // Grace period: treat past_due as active for up to 3 days
    if (subscription.status === "past_due" && isInGracePeriod(subscription.updated_at)) return true;
    return false;
  }, [subscription]);

  const isTrial = useCallback(() => {
    // While still loading, don't assume free plan
    if (!subscription && loading) return false;
    // No subscription record means free plan
    if (!subscription) return true;
    // Inactive subscriptions (incomplete, canceled, etc.) = free plan
    const inactiveStatuses = ['incomplete', 'incomplete_expired', 'canceled', 'unpaid'];
    if (inactiveStatuses.includes(subscription.status)) return true;
    // past_due within grace period retains paid access; otherwise reverts to free
    if (subscription.status === 'past_due' && !isInGracePeriod(subscription.updated_at)) return true;
    // Explicit free plan
    return subscription.plan === "free" && subscription.status === "active";
  }, [subscription, loading]);

  const getProjectLimit = useCallback(() => {
    if (isTrial()) return 1;
    if (isLite()) return 5;
    if (isStandard() || isPremium() || isPro()) return Infinity;
    // While subscription is still loading (user exists but subscription not yet fetched),
    // return free-tier default to avoid flash of "no access" state
    if (!subscription && user) return 1;
    return 0; // No access
  }, [subscription, user, isTrial, isLite, isStandard, isPremium, isPro]);

  const getOfferLimit = useCallback(() => {
    if (isTrial()) return 1;
    if (isLite()) return 1;
    if (isStandard()) return 1;
    if (isPremium() || isPro()) return Infinity;
    if (!subscription && user) return 1;
    return 0; // No access
  }, [subscription, user, isTrial, isLite, isStandard, isPremium, isPro]);

  const canGenerateOffers = useCallback(() => {
    return getOfferLimit() > 0;
  }, [getOfferLimit]);

  const getPlanName = useCallback(() => {
    const periodLabel = subscription?.period === "onetime"
      ? "1-Year"
      : subscription?.period === "yearly" ? "Yearly" : "Monthly";
    if (isTrial()) return "Free";
    if (isLite()) return `Lite (${periodLabel})`;
    if (isStandard()) return `Standard (${periodLabel})`;
    if (isPremium()) return `Premium (${periodLabel})`;
    // Enterprise must be checked before Pro: isPro() is true for both.
    if (isEnterprise()) return `Enterprise (${periodLabel})`;
    if (isPro()) return `Pro (${periodLabel})`;
    return "Free";
  }, [subscription, isTrial, isLite, isStandard, isPremium, isPro, isEnterprise]);

  return {
    subscription,
    isOrgSeat,
    fetchSubscription,
    clearSubscription,
    getTrialDaysRemaining,
    isTrialExpired,
    isLite,
    isStandard,
    isPremium,
    isPro,
    isEnterprise,
    isPaid,
    isTrial,
    getProjectLimit,
    getOfferLimit,
    canGenerateOffers,
    getPlanName,
  };
};
